import {
    SelectedItem,
    UserSelectionItem
} from '@/features/users/components/users-and-groups-multi-select-popover/UsersAndGroupsMultiSelectPopover'
import {CombinedItem} from '@/features/users/hooks/useUsersAndGroupsSearchableList'
import {GroupMember} from '@/features/users/types'
import {useState} from 'react'
import {FormProvider, UseFormReturn} from 'react-hook-form'
import {NewConversationFields} from '@/features/conversations/components/new-conversation-fields/NewConversationFields'
import {NewConversationFormValidationSchema} from '@/features/conversations/types'
import {useConfirmationDialogStore} from './confirmationDialogStore'
import {useShallow} from 'zustand/react/shallow'
import {StyledConfirmationDialogConversation} from './style'

export const ConfirmationDialogConversation = ({
    conversationForm
}: {
    conversationForm: UseFormReturn<NewConversationFormValidationSchema>
}) => {
    const {selectedMembers, setSelectedMembers} = useConfirmationDialogStore(
        useShallow(state => ({
            selectedMembers: state.selectedMembers,
            setSelectedMembers: state.setSelectedMembers
        }))
    )

    const [selectedItems, setSelectedItems] = useState<SelectedItem[]>([])
    const [selectedItemsData, setSelectedItemsData] = useState<Map<string, {name: string; type: 'user' | 'group'}>>(
        new Map()
    )

    const handleParticipantToggle = (users: UserSelectionItem[], itemData: CombinedItem) => {
        // Determine if we're adding or removing based on the first user's selection status
        const firstUser = users[0]
        if (!firstUser) return

        const isAdding = !selectedItems.some(item => item.type === 'user' && item.id === firstUser.id)

        // Update selected items - always work with users only
        setSelectedItems(prev => {
            if (isAdding) {
                // Add all users from the selection
                const newUserItems = users.map(user => ({
                    type: 'user' as const,
                    id: user.id
                }))

                // Remove any existing items that might conflict
                const filteredPrev = prev.filter(
                    item => !users.some(user => item.type === 'user' && item.id === user.id)
                )

                return [...filteredPrev, ...newUserItems]
            } else {
                // Remove all users from the selection
                return prev.filter(item => !users.some(user => item.type === 'user' && item.id === user.id))
            }
        })

        // Update item data for display
        setSelectedItemsData(prev => {
            const newMap = new Map(prev)

            if (isAdding) {
                // Add data for all users
                users.forEach(user => {
                    const name =
                        itemData.type === 'user'
                            ? itemData.fullName || itemData.name
                            : itemData.members?.find(m => m.id === user.id)?.fullName || `User ${user.id}`
                    newMap.set(user.id, {
                        name: name,
                        type: 'user' as const
                    })
                })
            } else {
                // Remove data for all users
                users.forEach(user => {
                    newMap.delete(user.id)
                })
            }
            return newMap
        })

        // Update form field with all selected user IDs
        const newSelectedItems = isAdding
            ? [...selectedItems, ...users.map(user => ({type: 'user' as const, id: user.id}))]
            : selectedItems.filter(item => !users.some(user => item.type === 'user' && item.id === user.id))

        conversationForm.setValue(
            'memberIds',
            newSelectedItems.map(item => item.id),
            {shouldValidate: true}
        )

        // Update selectedMembers for display compatibility
        if (isAdding) {
            // Add users
            const newMembers = users.map(user => {
                const nameParts = (
                    itemData.type === 'user'
                        ? itemData.fullName || itemData.name
                        : itemData.members?.find(m => m.id === user.id)?.fullName || `User ${user.id}`
                ).split(' ')
                return {
                    id: user.id,
                    firstName: nameParts[0] || 'User',
                    lastName: nameParts.slice(1).join(' ') || '',
                    email: itemData.type === 'user' ? itemData.email || '' : '',
                    avatarUrl: itemData.type === 'user' ? itemData.avatarUrl || null : null,
                    type: 'user' as const
                } as GroupMember & {type: 'user'}
            })
            setSelectedMembers([...selectedMembers, ...newMembers])
        } else {
            // Remove users
            const newMembers = selectedMembers.filter(member => !users.some(user => member.id === user.id))
            setSelectedMembers(newMembers)
        }
    }

    // Calculate display items for avatars (only users now)
    const currentDisplayItems = Array.from(selectedItemsData.entries()).map(([id, data]) => ({
        id,
        name: data.name,
        type: 'user' as const, // Always 'user' now since we only work with users
        avatarUrl: null
    }))

    return (
        <StyledConfirmationDialogConversation direction="column" fullWidth>
            <FormProvider {...conversationForm}>
                <NewConversationFields
                    selectedItems={selectedItems}
                    handleParticipantToggle={handleParticipantToggle}
                    currentDisplayItems={currentDisplayItems}
                />
            </FormProvider>
        </StyledConfirmationDialogConversation>
    )
}
